import { TemplateElement, html, defineElement } from '@webtides/element-js';
import style from './rive-animation.css';

/**
 * @property {string} src - URL to the .riv file
 * @property {string} artboard - Artboard name
 * @property {string} stateMachine - State machine name
 * @property {'contain'|'cover'|'fill'|'none'} fit - Layout fit mode
 * @property {string} alignment - Layout alignment
 * @property {string} aspectRatio - CSS aspect-ratio value
 * @property {'onView'|'autoplay'|'onClick'} trigger - When to start the animation
 * @property {boolean} pauseOutOfView - Pause animation when scrolled out of view
 */
export default class RiveAnimation extends TemplateElement {
    _rive = null;
    _resizeObserver = null;
    _intersectionObserver = null;
    _lazyLoadObserver = null;
    _loaded = false;
    _isIntersecting = false;

    constructor() {
        super({ styles: [style] });
    }

    properties() {
        return {
            src: '',
            artboard: '',
            stateMachine: '',
            fit: 'contain',
            alignment: 'center',
            aspectRatio: '',
            trigger: 'onView',
            pauseOutOfView: true,
        };
    }

    template() {
        const canvasClass = this.aspectRatio ? 'block w-full' : 'block w-full h-full';
        const canvasStyle = this.aspectRatio ? `aspect-ratio: ${this.aspectRatio}` : '';
        return html`<canvas ref="canvas" class="${canvasClass}" style="${canvasStyle}"></canvas>`;
    }

    _onVisibilityChange(isIntersecting) {
        if (!this._rive || !this._loaded) return;

        if (isIntersecting) {
            this._rive.play();
        } else if (this.pauseOutOfView) {
            this._rive.pause();
        }
    }

    connected() {
        if (!this.src) return;

        const canvas = this.$refs.canvas;
        if (!canvas) return;

        this._lazyLoadObserver = new IntersectionObserver(
            (entries) => {
                if (!entries[0].isIntersecting) return;
                this._lazyLoadObserver?.disconnect();
                this._lazyLoadObserver = null;
                this._init(canvas);
            },
            { rootMargin: '300px' },
        );
        this._lazyLoadObserver.observe(this);
    }

    async _init(canvas) {
        const { Rive, Layout, Fit, Alignment } = await import('@rive-app/webgl2');
        if (!this.isConnected) return;

        const fitMap = {
            contain: Fit.Contain,
            cover: Fit.Cover,
            fill: Fit.Fill,
            none: Fit.None,
        };

        const alignmentMap = {
            center: Alignment.Center,
            topCenter: Alignment.TopCenter,
            topLeft: Alignment.TopLeft,
            topRight: Alignment.TopRight,
            bottomCenter: Alignment.BottomCenter,
            bottomLeft: Alignment.BottomLeft,
            bottomRight: Alignment.BottomRight,
            centerLeft: Alignment.CenterLeft,
            centerRight: Alignment.CenterRight,
        };

        const options = {
            src: this.src,
            canvas,
            autoplay: this.trigger === 'autoplay',
            layout: new Layout({
                fit: fitMap[this.fit] ?? Fit.Contain,
                alignment: alignmentMap[this.alignment] ?? Alignment.Center,
            }),
            onLoad: () => {
                this._loaded = true;
                this._rive.resizeDrawingSurfaceToCanvas();
                if (this.trigger === 'onView' && this._isIntersecting) {
                    this._rive.play();
                }
            },
        };

        if (this.artboard) options.artboard = this.artboard;
        if (this.stateMachine) options.stateMachines = this.stateMachine;

        this._rive = new Rive(options);

        this._resizeObserver = new ResizeObserver(() => this._rive?.resizeDrawingSurfaceToCanvas());
        this._resizeObserver.observe(canvas);

        if (this.trigger === 'onView' || this.pauseOutOfView) {
            this._intersectionObserver = new IntersectionObserver(
                (entries) => {
                    this._isIntersecting = entries[0].isIntersecting;
                    this._onVisibilityChange(entries[0].isIntersecting);
                },
                { threshold: 0.5 },
            );
            this._intersectionObserver.observe(this);
        }

        if (this.trigger === 'onClick') {
            canvas.addEventListener('click', () => this._rive?.play());
        }
    }

    disconnected() {
        this._lazyLoadObserver?.disconnect();
        this._lazyLoadObserver = null;
        this._intersectionObserver?.disconnect();
        this._resizeObserver?.disconnect();
        this._rive?.cleanup();
        this._rive = null;
    }
}

defineElement('mb-rive-animation', RiveAnimation);
